/**
 * Discussion thread for one extraction row — a compliance requirement (spec C4)
 * or a risk-register item.
 *
 * Such a thread is whole-object: there is no prose to anchor into, so every
 * comment carries the all-null anchor and the whole surface is one flat list.
 * It reuses the drafting comment components verbatim — only the target family
 * differs, and that arrives as {@link RequirementDiscussionProps.targetType}.
 *
 * The composer is *pinned* to the bottom of the drawer rather than sitting
 * under the thread, so "leave a note" is reachable without scrolling past the
 * assessment and the source quote. The drawer owns that footer, so it passes
 * the element in as {@link RequirementDiscussionProps.composerSlot} and the
 * composer is portalled into it; without a slot the composer renders inline,
 * which is what every test and any future embed gets.
 */

import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { CommentCard } from '@/components/drafting/CommentCard'
import { CommentComposer } from '@/components/drafting/CommentComposer'
import { useBidMembers } from '@/hooks/useBidMembers'
import { useCommentsPolling } from '@/hooks/useCommentsPolling'
import { useCommentsStore } from '@/stores/comments-store'
import type {
  Comment,
  CommentAnchor,
  ExtractionCommentTargetType,
} from '@/lib/comments-api'

// ===================
// Constants
// ===================

/**
 * The only anchor a requirement comment may carry. The store guards this shape
 * and throws on anything else, so it is a module constant rather than an
 * object literal that could drift at a call site.
 */
const WHOLE_OBJECT_ANCHOR: CommentAnchor = {
  sectionId: null,
  startPos: null,
  endPos: null,
  quotedText: null,
}

const EMPTY_COMMENTS: readonly never[] = Object.freeze([])

// ===================
// Types
// ===================

export interface RequirementDiscussionProps {
  bidId: string
  /** The extraction item this thread hangs off. */
  itemId: string
  /**
   * Which extraction comment family the item belongs to. Defaults to the
   * compliance one, so existing call sites keep working untouched.
   */
  targetType?: ExtractionCommentTargetType
  readOnly: boolean
  /**
   * Pinned footer element to portal the composer into. Omitted (or not yet
   * measured) ⇒ the composer renders inline under the thread.
   */
  composerSlot?: HTMLElement | null
}

// ===================
// Component
// ===================

/**
 * Open threads first, then resolved; oldest first inside each group.
 *
 * The repository already returns this order, but a locally-created comment is
 * appended to the end of the list and a locally-resolved one keeps its slot —
 * so without this an optimistic mutation would visibly reorder the thread.
 */
function sortThreads(comments: readonly Comment[]): Comment[] {
  return [...comments].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'open' ? -1 : 1
    return a.createdAt.localeCompare(b.createdAt)
  })
}

export function RequirementDiscussion({
  bidId,
  itemId,
  targetType = 'requirement',
  readOnly,
  composerSlot,
}: RequirementDiscussionProps) {
  const commentsMap = useCommentsStore((s) => s.byTargetId)
  const comments = (commentsMap[itemId] ?? EMPTY_COMMENTS) as readonly Comment[]
  const loading = useCommentsStore((s) => s.loading[itemId] ?? false)
  const error = useCommentsStore((s) => s.error[itemId] ?? null)
  const permissionsMap = useCommentsStore((s) => s.permissions)
  const loadPermissions = useCommentsStore((s) => s.loadPermissions)
  const markViewed = useCommentsStore((s) => s.markViewed)
  const clearViewSnapshot = useCommentsStore((s) => s.clearViewSnapshot)

  /**
   * The cut-line is only knowable once the list GET has answered. Opening a
   * view session before then would snapshot a watermark of `null` and light
   * every comment ever written as "New" — the same gate the AI panel applies
   * to draft threads.
   */
  const watermarkLoaded = useCommentsStore((s) =>
    Object.prototype.hasOwnProperty.call(s.watermarkByTargetId, itemId),
  )

  const members = useBidMembers(bidId)

  // Remounts the editor after a save or a Cancel, which is what clears it.
  const [composerKey, setComposerKey] = useState(0)

  // Loud first load + 30 s silent ticks. The hook never opens a view session —
  // that is this component's job, below.
  useCommentsPolling(bidId, itemId, { targetType })

  useEffect(() => {
    if (!bidId || !itemId) return
    void loadPermissions(bidId, itemId, undefined, targetType)
  }, [bidId, itemId, targetType, loadPermissions])

  /**
   * The view session lasts exactly as long as the drawer shows this
   * requirement. Opening it snapshots the cut-line and POSTs
   * `comments-viewed`; the polling hook re-POSTs from there. Closing the
   * drawer — or moving to another row — closes it, so the next visit
   * re-snapshots against the advanced watermark and the pills clear.
   */
  useEffect(() => {
    if (!bidId || !itemId || !watermarkLoaded) return

    const open = (): void => {
      // A backgrounded browser tab is not a reader.
      if (document.visibilityState !== 'visible') return
      void markViewed(bidId, itemId, targetType)
    }

    open()
    document.addEventListener('visibilitychange', open)
    return () => {
      document.removeEventListener('visibilitychange', open)
      clearViewSnapshot(itemId)
    }
  }, [bidId, itemId, targetType, watermarkLoaded, markViewed, clearViewSnapshot])

  const threads = useMemo(() => sortThreads(comments), [comments])
  const openCount = threads.filter((c) => c.status === 'open').length

  const perms = permissionsMap[`${itemId}:__artifact__`]
  const showComposer = !readOnly && (perms?.canCreate ?? false)

  const composer = showComposer ? (
    <CommentComposer
      key={composerKey}
      bidId={bidId}
      targetId={itemId}
      targetType={targetType}
      anchor={WHOLE_OBJECT_ANCHOR}
      bidMembers={members}
      autoFocus={false}
      onClose={() => setComposerKey((k) => k + 1)}
    />
  ) : null

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Discussion</h3>
        {openCount > 0 && <span className="text-meta">{openCount} open</span>}
      </div>

      {/*
        Error is checked BEFORE empty: a failed load also leaves the list
        empty, and "No discussion yet." would then claim, falsely, that nobody
        has said anything. Never report absence we could not verify.
      */}
      {error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : loading && threads.length === 0 ? (
        <p className="text-center text-meta">Loading discussion…</p>
      ) : threads.length === 0 ? (
        <p className="text-center text-meta">No discussion yet.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {threads.map((c) => (
            <CommentCard
              key={c.id}
              comment={c}
              bidId={bidId}
              targetId={itemId}
              targetType={targetType}
              bidMembers={members}
            />
          ))}
        </div>
      )}

      {composerSlot && composer ? createPortal(composer, composerSlot) : composer}
    </div>
  )
}
